import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { Router } from "@angular/router";

import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { ApiResponseData } from "#interfaces/http-response/responses/http-responses";
import {
  GlobalSearch,
  GlobalSearchGroup,
  GlobalSearchGroupKey,
  GlobalSearchItem,
} from "#interfaces/search/global-search.interface";
import { FeatureFlagEnum } from "#root/config/enums/feature-flag.enum";
import { FeatureFlagService } from "#root/config/services/feature-flag/feature-flag.service";
import { AuthService } from "#root/core/auth/auth.service";
import {
  GLOBAL_SEARCH_SCREENS,
  GlobalSearchAccess,
  GlobalSearchFunctionality,
  GlobalSearchScreen,
} from "#root/core/layout/common/global-search/global-search-functionalities";
import { getUserPermissionMap, Permissions } from "#root/shared/enums/permission.enum";
import { RouteEnum } from "#root/shared/enums/route-permissions";
import { DashboardService } from "#services/dashboard/dashboard.service";
import { GlobalSearchService } from "#services/search/global-search.service";

type GlobalSearchOption =
  | { type: "item"; groupKey: GlobalSearchGroupKey; item: GlobalSearchItem }
  | { type: "viewAll"; groupKey: GlobalSearchGroupKey }
  | { type: "route"; route: string }
  | { type: "allProjects" };

interface GlobalSearchRequest {
  term: string;
  allProjects: boolean;
}

interface GlobalSearchScreenMatch {
  screen: GlobalSearchScreen;
  functionalities: GlobalSearchFunctionality[];
}

@Component({
  selector: "global-search",
  templateUrl: "./global-search.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @ViewChild("searchInput") private readonly searchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) private readonly autocompleteTrigger: MatAutocompleteTrigger;

  readonly minLength = 3;
  readonly maxScreens = 5;
  readonly maxFunctionalities = 5;
  placeholder = "Buscar funcionalidades…";
  ariaLabel = "Buscar funcionalidades";
  readonly shortcutLabel = /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent) ? "⌘ K" : "Ctrl K";

  searchControl = new FormControl<string | GlobalSearchOption>("");
  groups: GlobalSearchGroup[] = [];
  screens: GlobalSearchScreenMatch[] = [];
  term = "";
  allProjects = false;
  focused = false;
  focusLabel: string | null = null;
  isLoading = false;
  hasError = false;
  hasSearched = false;

  readonly groupIcons: Record<GlobalSearchGroupKey, string> = {
    paymentOrders: "heroicons_outline:currency-dollar",
    reserves: "heroicons_outline:bookmark",
    opportunities: "heroicons_outline:briefcase",
    customers: "heroicons_outline:user",
    prospects: "heroicons_outline:user-add",
    products: "heroicons_outline:home",
  };

  private readonly detailRoutes: Partial<Record<GlobalSearchGroupKey, string>> = {
    paymentOrders: RouteEnum.CRM_PAYMENT_ORDERS_DETAIL,
    reserves: RouteEnum.CRM_OPPORTUNITY_DETAIL,
    opportunities: RouteEnum.CRM_OPPORTUNITY_DETAIL,
    customers: RouteEnum.CRM_CLIENTS_DETAIL,
    prospects: `${RouteEnum.CRM_PROSPECTS}/details/:id`,
  };

  private readonly listRoutes: Record<GlobalSearchGroupKey, { route: RouteEnum; param: string | null }> = {
    paymentOrders: { route: RouteEnum.CRM_PAYMENT_ORDERS, param: "search" },
    reserves: { route: RouteEnum.CRM_OPPORTUNITY, param: "parameter" },
    opportunities: { route: RouteEnum.CRM_OPPORTUNITY, param: "parameter" },
    customers: { route: RouteEnum.CRM_CLIENTS, param: "search" },
    prospects: { route: RouteEnum.CRM_PROSPECTS, param: "search" },
    products: { route: RouteEnum.INVENTORY_PRODUCTS, param: "search" },
  };

  private readonly searchableLabels: [Permissions, string][] = [
    [Permissions.CanViewPaymentOrders, "planes de pago"],
    [Permissions.CanViewOpportunities, "reservas"],
    [Permissions.CanViewOpportunities, "oportunidades"],
    [Permissions.CanViewCustomers, "clientes"],
    [Permissions.CanViewProspects, "leads"],
    [Permissions.CanViewProducts, "inmuebles"],
  ];

  private permissionsMap: ReturnType<typeof getUserPermissionMap> | null = null;
  private readonly searchRequest$ = new Subject<GlobalSearchRequest>();
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly globalSearchService: GlobalSearchService,
    private readonly dashboardService: DashboardService,
    private readonly authService: AuthService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.authService.loggedInUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user) => {
        this.permissionsMap = user?.permissionsMap ?? null;
        this.buildPlaceholder();
      });

    this.searchRequest$
      .pipe(
        switchMap((request) => this.search(request)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.searchControl.valueChanges
      .pipe(
        filter((value): value is string | null => typeof value === "string" || value === null),
        map((value) => (value ?? "").trim()),
        debounceTime(300),
        distinctUntilChanged((previous, current) => previous === current && !this.hasError),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((term) => {
        this.searchRequest$.next({ term, allProjects: false });
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener("document:keydown", ["$event"])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.key?.toLowerCase() !== "k" || event.defaultPrevented) {
      return;
    }

    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.isContentEditable || target?.closest("[contenteditable], .ql-editor")) {
      return;
    }

    event.preventDefault();
    this.searchInput?.nativeElement.focus();
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as GlobalSearchOption;

    if (option.type === "allProjects") {
      this.searchControl.setValue(this.term, { emitEvent: false });
      this.searchRequest$.next({ term: this.term, allProjects: true });
      setTimeout(() => this.autocompleteTrigger?.openPanel());
      return;
    }

    if (option.type === "route") {
      this.router.navigateByUrl(option.route);
    } else if (option.type === "item" && this.detailRoutes[option.groupKey]) {
      this.router.navigateByUrl(this.detailRoutes[option.groupKey].replace(":id", option.item.id));
    } else {
      this.navigateToList(option.groupKey);
    }

    this.searchControl.setValue("");
  }

  itemOption(groupKey: GlobalSearchGroupKey, item: GlobalSearchItem): GlobalSearchOption {
    return { type: "item", groupKey, item };
  }

  viewAllOption(groupKey: GlobalSearchGroupKey): GlobalSearchOption {
    return { type: "viewAll", groupKey };
  }

  routeOption(route: string): GlobalSearchOption {
    return { type: "route", route };
  }

  allProjectsOption(): GlobalSearchOption {
    return { type: "allProjects" };
  }

  get hasText(): boolean {
    const value = this.searchControl.value;
    return typeof value === "string" ? value.length > 0 : value !== null;
  }

  displayTerm = (value: string | GlobalSearchOption | null): string =>
    typeof value === "string" ? value : this.term;

  trackByGroup(_: number, group: GlobalSearchGroup): GlobalSearchGroupKey {
    return group.key;
  }

  trackByItem(_: number, item: GlobalSearchItem): string {
    return item.id;
  }

  trackByScreen(_: number, match: GlobalSearchScreenMatch): string {
    return match.screen.route;
  }

  trackByFunctionality(_: number, functionality: GlobalSearchFunctionality): string {
    return functionality.label;
  }

  private navigateToList(groupKey: GlobalSearchGroupKey): void {
    const list = this.listRoutes[groupKey];
    this.router.navigate([list.route], {
      queryParams: list.param ? { [list.param]: this.term } : {},
    });
  }

  private hasAccess(access: GlobalSearchAccess | null, featureFlag?: FeatureFlagEnum): boolean {
    if (featureFlag && !this.featureFlagService.isEnabled(featureFlag)) {
      return false;
    }

    if (!access) {
      return true;
    }

    const permissionsMap = this.permissionsMap;
    if (!permissionsMap) {
      return false;
    }

    return access.mode === "any"
      ? access.permissions.some((permission) => permissionsMap[permission])
      : access.permissions.every((permission) => permissionsMap[permission]);
  }

  private matches(term: string, texts: string[]): boolean {
    return texts.some((text) => this.normalize(text).includes(term));
  }

  private matchScreens(term: string): GlobalSearchScreenMatch[] {
    return GLOBAL_SEARCH_SCREENS.reduce<GlobalSearchScreenMatch[]>((result, screen) => {
      if (!this.hasAccess(screen.access, screen.featureFlag)) {
        return result;
      }

      const functionalities = screen.functionalities
        .filter((functionality) =>
          this.hasAccess(functionality.access, functionality.featureFlag) &&
          this.matches(term, [functionality.label, ...functionality.keywords]),
        )
        .slice(0, this.maxFunctionalities);

      if (functionalities.length || this.matches(term, [screen.title, ...screen.keywords])) {
        result.push({ screen, functionalities });
      }

      return result;
    }, []).slice(0, this.maxScreens);
  }

  private buildPlaceholder(): void {
    const labels = this.searchableLabels
      .filter(([permission]) => this.permissionsMap?.[permission])
      .map(([, label]) => label)
      .filter((label, index, all) => all.indexOf(label) === index);

    this.placeholder = labels.length ? `Buscar ${labels.slice(0, 3).join(", ")}…` : "Buscar funcionalidades…";
    this.ariaLabel = `Buscar ${[...labels, "funcionalidades"].join(", ")}`;
  }

  private normalize(value: string): string {
    return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  }

  private search(request: GlobalSearchRequest): Observable<void> {
    this.term = request.term;
    this.allProjects = request.allProjects;
    this.groups = [];
    this.screens = [];
    this.focused = false;
    this.focusLabel = null;
    this.hasError = false;
    this.hasSearched = false;

    if (request.term.length < this.minLength) {
      this.isLoading = false;
      return of(undefined);
    }

    this.screens = this.matchScreens(this.normalize(request.term));

    this.isLoading = true;
    const projectId = this.dashboardService.currenProjectId;

    return this.globalSearchService
      .Global({
        term: request.term,
        projectId: projectId > 0 ? projectId : null,
        allProjects: request.allProjects,
      })
      .pipe(
        map((response: ApiResponseData<GlobalSearch>) => {
          this.groups = (response.data?.groups ?? []).filter((group) => group.items.length > 0);
          this.focused = response.data?.focused ?? false;
          this.focusLabel = response.data?.focusLabel ?? null;
          this.hasSearched = true;
          this.isLoading = false;
        }),
        catchError(() => {
          this.hasError = true;
          this.isLoading = false;
          return of(undefined);
        }),
      );
  }
}
